import React from 'react';
import { useLayout } from '../../../context/LayoutContext';
import '../../../assets/scss/components/wssp/ChatList.scss';
import { useWhatsApp } from '../../../context/WhatsAppContext';

const ChatList = () => {
  const { theme } = useLayout();
  const { 
    chats = [], 
    selectedChat, 
    selectChat, 
    filteredChats = [], 
    loading = false,
    authenticated = false,
    searchQuery = ''
  } = useWhatsApp();
  
  // Protección contra valores undefined y determinación de chats a mostrar
  const chatsToDisplay = React.useMemo(() => {
    if (searchQuery && filteredChats.length > 0) {
      return filteredChats;
    }
    return chats;
  }, [chats, filteredChats, searchQuery]);
  
  const formatLastMessageTime = (timestamp) => {
    if (!timestamp) return '';
    
    try {
      const now = new Date();
      const messageDate = new Date(timestamp);
      
      // Verificar que la fecha sea válida
      if (isNaN(messageDate.getTime())) return '';
      
      const diffInHours = (now - messageDate) / (1000 * 60 * 60);
      
      if (diffInHours < 24) {
        return messageDate.toLocaleTimeString('es-ES', { 
          hour: '2-digit', 
          minute: '2-digit' 
        });
      } else if (diffInHours < 24 * 7) {
        return messageDate.toLocaleDateString('es-ES', { weekday: 'short' });
      } else {
        return messageDate.toLocaleDateString('es-ES', { 
          day: '2-digit', 
          month: '2-digit' 
        });
      }
    } catch (error) {
      console.warn('Error formatting timestamp:', error);
      return '';
    }
  };
  
  const truncateMessage = (message, maxLength = 35) => {
    if (!message || typeof message !== 'string') return 'Sin mensajes';
    if (message.length <= maxLength) return message;
    return message.substring(0, maxLength) + '...';
  };
  
  const getContactInitials = (name) => {
    if (!name || typeof name !== 'string') return '?';
    const words = name.trim().split(/\s+/);
    if (words.length >= 2) {
      return (words[0][0] + words[1][0]).toUpperCase();
    }
    return name.charAt(0).toUpperCase();
  };
  
  // Manejo de estados de carga
  if (loading && !authenticated) {
    return (
      <div className={`GM__${theme}-wssp-chat-list`}>
        <div className={`GM__${theme}-wssp-loading-state`}>
          <p>Inicializando WhatsApp...</p>
        </div>
      </div>
    );
  }
  
  if (authenticated && chatsToDisplay.length === 0 && !searchQuery) {
    return (
      <div className={`GM__${theme}-wssp-chat-list`}>
        <div className={`GM__${theme}-wssp-loading-state`}>
          <p>Cargando chats...</p>
        </div>
      </div>
    );
  }
  
  // Estado vacío con búsqueda
  if (chatsToDisplay.length === 0) {
    return (
      <div className={`GM__${theme}-wssp-chat-list`}>
        <div className={`GM__${theme}-wssp-empty-state`}>
          <p>
            {searchQuery 
              ? 'No se encontraron resultados para tu búsqueda' 
              : 'No hay chats disponibles'}
          </p>
        </div>
      </div>
    );
  }
  
  return (
    <div className={`GM__${theme}-wssp-chat-list`}>
      {chatsToDisplay.map((chat) => {
        // Verificaciones de seguridad para cada chat
        const chatId = chat?.id || `chat-${Math.random()}`;
        const chatName = chat?.name || 'Chat sin nombre';
        const lastMessage = chat?.lastMessage || null;
        const unreadCount = chat?.unreadCount || 0;
        const isPinned = Boolean(chat?.isPinned);
        const profilePicUrl = chat?.profilePicUrl || null;
        const lastMessageTime = chat?.lastMessageTime || chat?.timestamp || null;
        
        return (
          <div
            key={chatId}
            className={`GM__${theme}-wssp-chat-item ${
              selectedChat?.id === chatId ? 'active' : ''
            }`}
            onClick={() => selectChat && selectChat(chat)}
          >
            <div className={`GM__${theme}-wssp-chat-avatar`}>
              {profilePicUrl ? (
                <img 
                  src={profilePicUrl} 
                  alt={chatName}
                  onError={(e) => {
                    e.target.style.display = 'none';
                    const fallback = e.target.nextSibling;
                    if (fallback) {
                      fallback.style.display = 'flex';
                    }
                  }}
                />
              ) : null}
              <div 
                className={`GM__${theme}-wssp-avatar-fallback`}
                style={{ display: profilePicUrl ? 'none' : 'flex' }}
              >
                {getContactInitials(chatName)}
              </div>
            </div>
            
            <div className={`GM__${theme}-wssp-chat-info`}>
              <div className={`GM__${theme}-wssp-chat-header`}>
                <h4 className={`GM__${theme}-wssp-chat-name`}>
                  {chatName}
                </h4>
                <span className={`GM__${theme}-wssp-chat-time`}>
                  {formatLastMessageTime(lastMessageTime)}
                </span>
              </div>
              
              <div className={`GM__${theme}-wssp-chat-preview`}>
                <p className={`GM__${theme}-wssp-last-message`}>
                  {lastMessage?.fromMe ? '✓ ' : ''}
                  {lastMessage?.hasMedia ? '📎 Media' : truncateMessage(lastMessage?.body)}
                </p>
                
                {unreadCount > 0 && (
                  <span className={`GM__${theme}-wssp-unread-badge`}>
                    {unreadCount > 99 ? '99+' : unreadCount}
                  </span>
                )}
              </div>
            </div>
            
            {isPinned && (
              <div className={`GM__${theme}-wssp-pin-indicator`}>📌</div>
            )}
          </div>
        );
      })}
    </div>
  );
};

export default ChatList;